import Checkbox from '@repo/ui/checkbox'
import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getMissionTypes } from '../../../api/mission-types/get-mission-types'
import { getPeriods } from '../../../api/periods/get-periods'
import {
  StaffPaymentStatusEnum,
  staffPaymentStatusEnumMapping
} from '../../../lib/types/enums/affected-staff/staff-payment-status.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { MissionToStaffSalaryFilter } from '../../../lib/types/models/mission-tracking/mission-tracking'

interface Props {
  filter?: MissionToStaffSalaryFilter
  onFilterChange: (filter: MissionToStaffSalaryFilter) => void
}

export function MissionTrackingSalaryFilters({ filter, onFilterChange }: Props) {
  const { t } = useTranslation()

  function setFilter<K extends keyof MissionToStaffSalaryFilter>(
    key: K,
    value: MissionToStaffSalaryFilter[K]
  ) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: missionTypes } = useQuery({
    queryKey: ['mission-types'],
    queryFn: () => getMissionTypes()
  })

  return (
    <div className={'flex flex-col gap-4 mt-2'}>
      <MultiSelectInput
        searchable
        label={'Période(s)'}
        variant={'warning'}
        options={periods?.data?.map((period) => ({ label: period.name, value: period.id }))}
        onChange={(periodIds) => setFilter('periodIds', periodIds)}
        selectedOptions={filter?.periodIds?.map((periodId) => ({
          label: periods?.data?.find((p) => p.id === periodId)?.name || '',
          value: periodId
        }))}
      />

      <MultiSelectInput
        searchable
        label={'Qualifications'}
        variant={'primary'}
        options={Object.values(StaffQualificationEnum).map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
        onChange={(qualifications) => setFilter('qualifications', qualifications)}
        selectedOptions={filter?.qualifications?.map((qualification) => ({
          label: t(staffQualificationEnumMapping[qualification].label),
          value: qualification
        }))}
      />

      <MultiSelectInput
        label={'Poste'}
        variant={'primary'}
        options={Object.values(StaffPositionEnum).map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
        onChange={(positions) => setFilter('positions', positions)}
        selectedOptions={filter?.positions?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
      />

      <div className="grid grid-cols-2 gap-4">
        <DateStringInput
          label={'Mission après le'}
          value={filter?.missionAfterDate}
          onChange={(date) => setFilter('missionAfterDate', date ?? undefined)}
        />
        <DateStringInput
          label={'Mission avant le'}
          value={filter?.missionBeforeDate}
          onChange={(date) => setFilter('missionBeforeDate', date ?? undefined)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <DateStringInput
          label={'Contrat après le'}
          value={filter?.contractAfterDate}
          onChange={(date) => setFilter('contractAfterDate', date ?? undefined)}
        />
        <DateStringInput
          label={'Contrat avant le'}
          value={filter?.contractBeforeDate}
          onChange={(date) => setFilter('contractBeforeDate', date ?? undefined)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <DateStringInput
          label={'Affecté après le'}
          value={filter?.affectedAfterDate}
          onChange={(date) => setFilter('affectedAfterDate', date ?? undefined)}
        />
        <DateStringInput
          label={'Affecté avant le'}
          value={filter?.affectedBeforeDate}
          onChange={(date) => setFilter('affectedBeforeDate', date ?? undefined)}
        />
      </div>
      <MultiSelectInput
        label={'Types de mission'}
        variant={'primary'}
        options={missionTypes?.data?.map((missionType) => ({
          label: missionType.title,
          value: missionType.id
        }))}
        onChange={(missionTypeIds) => setFilter('missionTypeIds', missionTypeIds)}
        selectedOptions={filter?.missionTypeIds?.map((missionTypeId) => ({
          label: missionTypes?.data?.find((mt) => mt.id === missionTypeId)?.title || '',
          value: missionTypeId
        }))}
      />
      <div className="flex flex-col gap-2">
        <Typography variant="label-medium">Statut du virement</Typography>

        <div className="flex flex-row gap-2">
          {Object.values(StaffPaymentStatusEnum).map((paymentStatus) => (
            <Checkbox
              key={paymentStatus}
              checked={filter?.paymentStatuses?.includes(paymentStatus)}
              label={staffPaymentStatusEnumMapping[paymentStatus].state}
              onChange={(checked) => {
                setFilter(
                  'paymentStatuses',
                  checked
                    ? [...(filter?.paymentStatuses || []), paymentStatus]
                    : filter?.paymentStatuses?.filter((t) => t !== paymentStatus)
                )
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
